import { Typography } from "@mui/material";
import {
  CUSTOMERS_STATUS_FILTER_OPTIONS,
  CustomersTable,
  CustomersToolbar,
  getThunkErrorMessage,
  ListFiltersDrawer,
  ListAppliedFilterChips,
  ListTablePaginationBar,
  type ListAppliedFilterChipItem,
  type CustomersToolbarLocationOption,
  type ToolbarSearchableSelectOption,
  useNotificationSnackbar,
  useLocationsListWithService,
  useMemoryLeakPrevention,
  CommonDropdownGenerators,
  useFilterValidation,
  buildMultiSelectFilterChips,
  mergeAppliedFilterChips,
} from "design-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InviteCustomerDrawer } from "../components/invite-customer-drawer.component";
import type {
  CustomerFormDrawerInitialValues,
  CustomerFormDrawerMode,
  CustomerFormSubmitPayload,
} from "../types";
import { consumeCustomersListFlashNotification } from "../utils/customers-list-flash-notification";
import { locationsService } from "../../../shared/services/locations.service";
import { useAppDispatch, useAppSelector } from "../../../shared/store/hook";
import { CUSTOMERS_LIST_PAGE_SIZE } from "../../../shared/store/customers/customers.models";
import {
  fetchCustomerForEdit,
  fetchCustomersList,
  inviteCustomer,
  updateCustomerFromList,
  setCustomersListFilters,
  setCustomersListLocationFilters,
  setCustomersListRange,
  setCustomersListSearch,
  setCustomersListSort,
} from "../../../shared/store/customers";
import {
  selectCustomersListEditPrefillLoading,
  selectCustomersListError,
  selectCustomersListFilterStatus,
  selectCustomersListFormSubmitting,
  selectCustomersListFromIndex,
  selectCustomersListLoading,
  selectCustomersListLocationFilterIds,
  selectCustomersListRows,
  selectCustomersListSearch,
  selectCustomersListSortField,
  selectCustomersListSortOrder,
  selectCustomersListTotal,
} from "../../../shared/store/customers/customers.selector";

/** Maps drawer `phone` to optional API `phoneNumber` (omit when empty). */
function toOptionalPhoneNumber(phone: string): string | undefined {
  const trimmed = phone.trim();
  return trimmed || undefined;
}

export const CustomersListPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const rows = useAppSelector(selectCustomersListRows);
  const loading = useAppSelector(selectCustomersListLoading);
  const error = useAppSelector(selectCustomersListError);
  const totalCustomers = useAppSelector(selectCustomersListTotal);
  const search = useAppSelector(selectCustomersListSearch);
  const fromIndex = useAppSelector(selectCustomersListFromIndex);

  const {
    locations: locationsFromApi,
    loading: locationsLoading,
  } = useLocationsListWithService(locationsService, {}, { immediate: true });

  const sortingOrder = useAppSelector(selectCustomersListSortOrder);
  const sortingField = useAppSelector(selectCustomersListSortField);
  const locationFilterIds = useAppSelector(selectCustomersListLocationFilterIds);
  const appliedListFilterStatus = useAppSelector(selectCustomersListFilterStatus);
  const customerFormSubmitting = useAppSelector(selectCustomersListFormSubmitting);
  const editPrefillLoading = useAppSelector(selectCustomersListEditPrefillLoading);

  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState<Record<string, string[]>>({});
  const [customerFormDrawerOpen, setCustomerFormDrawerOpen] = useState(false);
  const [customerFormDrawerMode, setCustomerFormDrawerMode] = useState<CustomerFormDrawerMode>("invite");
  const [editCustomerId, setEditCustomerId] = useState<number | null>(null);
  const [editInitialValues, setEditInitialValues] = useState<CustomerFormDrawerInitialValues | null>(null);
  const [editOverviewLocationOptions, setEditOverviewLocationOptions] = useState<ToolbarSearchableSelectOption[]>([]);

  const { showNotification, snackbar } = useNotificationSnackbar();

  useMemoryLeakPrevention();

  useEffect(() => {
    dispatch(fetchCustomersList());
  }, [dispatch]);

  useEffect(() => {
    const flash = consumeCustomersListFlashNotification(location.state);
    if (!flash) return;
    showNotification(flash);
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate, showNotification]);

  useEffect(() => {
    if (!error) return;
    showNotification({
      variant: "error",
      title: "Could not load customers",
      description: error,
    });
  }, [error, showNotification]);

  const resetListWindowFirstPage = useCallback(() => {
    dispatch(
      setCustomersListRange({
        fromIndex: 0,
        toIndex: CUSTOMERS_LIST_PAGE_SIZE,
      }),
    );
  }, [dispatch]);

  const fetchListFromFirstPageThunk = useCallback(() => {
    return (d: typeof dispatch) => {
      d(
        setCustomersListRange({
          fromIndex: 0,
          toIndex: CUSTOMERS_LIST_PAGE_SIZE,
        }),
      );
      return d(fetchCustomersList());
    };
  }, [dispatch]);

  const listFilterSections = useMemo(
    () => [{ id: "status", label: "Status", options: CUSTOMERS_STATUS_FILTER_OPTIONS }],
    [],
  );

  const locationRows = locationsFromApi?.data?.locations;

  const locationOptions: CustomersToolbarLocationOption[] = useMemo(() => {
    if (!Array.isArray(locationRows)) {
      return [{ value: "", label: "All locations" }];
    }
    const sorted = [...locationRows].sort((a, b) =>
      String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" }),
    );
    return CommonDropdownGenerators.locations(sorted);
  }, [locationRows]);

  const appliedChipItems: ListAppliedFilterChipItem[] = useMemo(() => {
    const items: ListAppliedFilterChipItem[] = [];

    for (const v of appliedListFilterStatus) {
      const option = CUSTOMERS_STATUS_FILTER_OPTIONS.find((opt) => opt.value === v);
      items.push({
        id: `status:${v}`,
        label: option?.label || v,
        onRemove: () => {
          dispatch(
            setCustomersListFilters({
              listFilterStatus: appliedListFilterStatus.filter((val) => val !== v),
            }),
          );
          resetListWindowFirstPage();
          dispatch(fetchCustomersList());
        },
      });
    }

    return mergeAppliedFilterChips(
      items,
      buildMultiSelectFilterChips({
        sectionId: "location",
        selectedIds: locationFilterIds,
        options: locationOptions,
        onRemoveId: (id) => {
          dispatch(setCustomersListLocationFilters(locationFilterIds.filter((v) => v !== id)));
          resetListWindowFirstPage();
          dispatch(fetchCustomersList());
        },
      }),
    );
  }, [appliedListFilterStatus, locationFilterIds, locationOptions, dispatch, resetListWindowFirstPage]);

  const handleClearAllChips = useCallback(() => {
    dispatch(
      setCustomersListFilters({
        listFilterStatus: [],
      }),
    );
    dispatch(setCustomersListLocationFilters([]));
    resetListWindowFirstPage();
    dispatch(fetchCustomersList());
  }, [dispatch, resetListWindowFirstPage]);

  const inviteDrawerLocationOptions = useMemo((): ToolbarSearchableSelectOption[] => {
    const byId = new Map<string, ToolbarSearchableSelectOption>();

    if (Array.isArray(locationRows)) {
      for (const loc of locationRows) {
        const value = String(loc.locationId);
        byId.set(value, {
          value,
          label: String(loc.label || "").trim() || `Location ${loc.locationId}`,
        });
      }
    }

    for (const opt of editOverviewLocationOptions) {
      if (!byId.has(opt.value)) {
        byId.set(opt.value, opt);
      }
    }

    return [...byId.values()].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
  }, [locationRows, editOverviewLocationOptions]);

  useFilterValidation({
    filterIds: locationFilterIds,
    options: locationOptions,
    setFilterAction: setCustomersListLocationFilters,
    fetchAction: fetchListFromFirstPageThunk,
    dispatch,
  });

  const editEnabled = selectedCustomerIds.length === 1;

  const listPage = Math.floor(fromIndex / CUSTOMERS_LIST_PAGE_SIZE) + 1;

  const handlePageChange = useCallback(
    (nextPage: number) => {
      const from = (nextPage - 1) * CUSTOMERS_LIST_PAGE_SIZE;
      dispatch(
        setCustomersListRange({
          fromIndex: from,
          toIndex: from + CUSTOMERS_LIST_PAGE_SIZE,
        }),
      );
      dispatch(fetchCustomersList());
    },
    [dispatch],
  );

  const handleSearchTrigger = useCallback(
    (value: string) => {
      dispatch(setCustomersListSearch(value));
      resetListWindowFirstPage();
      dispatch(fetchCustomersList());
    },
    [dispatch, resetListWindowFirstPage],
  );

  const handleSortFieldClick = useCallback(
    (field: string) => {
      const sameField = sortingField === field;
      const nextOrder = sameField ? (sortingOrder === "desc" ? "asc" : "desc") : "desc";
      dispatch(
        setCustomersListSort({
          sortingOrder: nextOrder,
          sortingField: field,
        }),
      );
      resetListWindowFirstPage();
      dispatch(fetchCustomersList());
    },
    [dispatch, resetListWindowFirstPage, sortingField, sortingOrder],
  );

  const handleLocationsChange = (nextIds: string[]) => {
    dispatch(setCustomersListLocationFilters(nextIds));
    setSelectedCustomerIds([]);
    resetListWindowFirstPage();
    dispatch(fetchCustomersList());
  };

  const openFilterDrawer = useCallback(() => {
    setFilterDraft({
      status: [...appliedListFilterStatus],
    });
    setFilterDrawerOpen(true);
  }, [appliedListFilterStatus]);

  const handleFilterDrawerClose = () => setFilterDrawerOpen(false);

  const handleFilterApply = useCallback(
    (applied: Record<string, string[]>) => {
      dispatch(
        setCustomersListFilters({
          listFilterStatus: applied.status ?? [],
        }),
      );
      setSelectedCustomerIds([]);
      resetListWindowFirstPage();
      dispatch(fetchCustomersList());
      setFilterDrawerOpen(false);
    },
    [dispatch, resetListWindowFirstPage],
  );

  const handleInviteClick = useCallback(() => {
    setCustomerFormDrawerMode("invite");
    setEditCustomerId(null);
    setEditInitialValues(null);
    setEditOverviewLocationOptions([]);
    setCustomerFormDrawerOpen(true);
  }, []);

  const handleCustomerFormDrawerClose = useCallback(() => {
    if (customerFormSubmitting) return;
    setCustomerFormDrawerOpen(false);
    setEditCustomerId(null);
    setEditInitialValues(null);
    setEditOverviewLocationOptions([]);
  }, [customerFormSubmitting]);

  const handleCustomerFormSubmit = useCallback(
    async (payload: CustomerFormSubmitPayload) => {
      try {
        if (customerFormDrawerMode === "invite") {
          await dispatch(
            inviteCustomer({
              companyName: payload.companyName,
              primaryContactName: payload.primaryContactName,
              emailId: payload.primaryContactEmail,
              phoneNumber: toOptionalPhoneNumber(payload.phone),
              customerAddress: payload.primaryBusinessAddress || undefined,
              locationIds: payload.locationIds,
            }),
          ).unwrap();
          setCustomerFormDrawerOpen(false);
          showNotification({
            variant: "success",
            title: "Customer invited",
            description: `${payload.companyName} has been invited.`,
          });
          return;
        }

        const customerId = payload.customerId ?? editCustomerId;
        if (!customerId) {
          showNotification({
            variant: "error",
            title: "Update failed",
            description: "Customer id is missing. Close the drawer and try again.",
          });
          return;
        }

        await dispatch(
          updateCustomerFromList({
            customerId,
            body: {
              companyName: payload.companyName,
              primaryContactName: payload.primaryContactName,
              phoneNumber: toOptionalPhoneNumber(payload.phone),
              customerAddress: payload.primaryBusinessAddress || undefined,
              locationIds: payload.locationIds,
            },
          }),
        ).unwrap();
        setCustomerFormDrawerOpen(false);
        setEditCustomerId(null);
        setEditInitialValues(null);
        setEditOverviewLocationOptions([]);
        showNotification({
          variant: "success",
          title: "Customer updated",
          description: `${payload.companyName} has been updated.`,
        });
      } catch (e: unknown) {
        const isInvite = customerFormDrawerMode === "invite";
        showNotification({
          variant: "error",
          title: isInvite ? "Invite failed" : "Update failed",
          description: getThunkErrorMessage(
            e,
            isInvite ? "Could not send the invitation." : "Could not update the customer.",
          ),
        });
      }
    },
    [customerFormDrawerMode, dispatch, editCustomerId, showNotification],
  );

  const handleEditClick = useCallback(async () => {
    if (!editEnabled || selectedCustomerIds.length !== 1) return;

    const customerId = Number(selectedCustomerIds[0]);
    if (!Number.isFinite(customerId)) return;

    setCustomerFormDrawerMode("edit");
    setEditCustomerId(customerId);
    setEditInitialValues(null);
    setEditOverviewLocationOptions([]);
    setCustomerFormDrawerOpen(true);

    try {
      const prefill = await dispatch(fetchCustomerForEdit(customerId)).unwrap();
      setEditInitialValues(prefill.initialValues);
      setEditOverviewLocationOptions(prefill.locationOptions);
    } catch (e: unknown) {
      setCustomerFormDrawerOpen(false);
      setEditCustomerId(null);
      setEditOverviewLocationOptions([]);
      showNotification({
        variant: "error",
        title: "Could not load customer",
        description: getThunkErrorMessage(e, "Could not load customer details for editing."),
      });
    }
  }, [dispatch, editEnabled, selectedCustomerIds, showNotification]);

  return (
    <div className="customers-page">
      <Typography variant="h5" className="customers-page__title" component="h1">
        Customers
      </Typography>
      <div className="users-page__main">
        <CustomersToolbar
          variant="operator"
          searchValue={search}
          onSearchTrigger={handleSearchTrigger}
          onFilterClick={openFilterDrawer}
          onInviteClick={handleInviteClick}
          onEditClick={handleEditClick}
          editEnabled={editEnabled}
          deleteEnabled={false}
          locationOptions={locationOptions}
          selectedLocationIds={locationFilterIds}
          onLocationsChange={handleLocationsChange}
          locationsLoading={locationsLoading}
        />
        <ListAppliedFilterChips
          items={appliedChipItems}
          onClearAll={handleClearAllChips}
          totalResults={totalCustomers}
        />
        <div className="users-page__table-scroll">
          <CustomersTable
            variant="operator"
            loading={loading}
            rows={error ? [] : rows}
            selectedCustomerIds={selectedCustomerIds}
            onSelectedCustomerIdsChange={setSelectedCustomerIds}
            sortField={sortingField}
            sortDescending={sortingOrder === "desc"}
            onSortFieldClick={handleSortFieldClick}
            getCustomerNameLink={(row) => ({
              to: `/customers/${row.id}`,
            })}
            paginationFooter={
              <ListTablePaginationBar
                totalCount={totalCustomers}
                page={listPage}
                pageSize={CUSTOMERS_LIST_PAGE_SIZE}
                onPageChange={handlePageChange}
                disabled={loading}
              />
            }
          />
        </div>
      </div>
      <InviteCustomerDrawer
        mode={customerFormDrawerMode}
        open={customerFormDrawerOpen}
        onClose={handleCustomerFormDrawerClose}
        locationOptions={inviteDrawerLocationOptions}
        locationsLoading={locationsLoading}
        initialValues={editInitialValues}
        prefillLoading={editPrefillLoading}
        customerId={editCustomerId ?? undefined}
        onSubmit={handleCustomerFormSubmit}
        submitting={customerFormSubmitting}
      />
      <ListFiltersDrawer
        open={filterDrawerOpen}
        onClose={handleFilterDrawerClose}
        title="Filters"
        subtitle="Select filters for customers."
        sections={listFilterSections}
        value={filterDraft}
        onChange={setFilterDraft}
        onApply={handleFilterApply}
      />
      {snackbar}
    </div>
  );
};
